import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { TextButton } from '@bibliocommons/base-buttons';
import { ChevronDown, ChevronUp } from '@bibliocommons/base-icons';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import './BibDescription.scss';

const MIN_WINDOW_WIDTH_PX_WIDE = 768;
const COLLAPSED_MAX_HEIGHT_PX_NARROW = 166;
const COLLAPSED_MAX_HEIGHT_PX_WIDE = 120;

const BibDescription = ({ descriptionHtml, uniqueIdPrefix }) => {
  const [collapsedHeightPx, setCollapsedHeightPx] = useState(COLLAPSED_MAX_HEIGHT_PX_NARROW);
  const expandableContainer = useRef(null);
  const htmlContainer = useRef(null);
  const expandableContainerId = `${uniqueIdPrefix}_Expandable`;
  const [isExpandable, setIsExpandable] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleClick = () => {
    setIsExpanded(!isExpanded);
  };

  useEffect(() => {
    const determineIfExpandable = () => {
      const htmlContainerHeightPx = htmlContainer?.current?.clientHeight;
      const newCollapsedHeightPx =
        window.innerWidth < MIN_WINDOW_WIDTH_PX_WIDE ? COLLAPSED_MAX_HEIGHT_PX_NARROW : COLLAPSED_MAX_HEIGHT_PX_WIDE;

      setCollapsedHeightPx(newCollapsedHeightPx);
      setIsExpandable(htmlContainerHeightPx > newCollapsedHeightPx);
    };

    const onWindowResize = debounce(determineIfExpandable, 100);

    window.addEventListener('resize', onWindowResize);

    determineIfExpandable();

    return () => {
      window.removeEventListener('resize', onWindowResize);
    };
  }, []);

  useEffect(() => {
    if (isExpanded) {
      return;
    }

    const expandableContainerOffsetPx = expandableContainer?.current?.getBoundingClientRect()?.top;

    const isToScroll = expandableContainerOffsetPx < 0;

    if (isToScroll) {
      expandableContainer?.current?.scrollIntoView();
    }
  }, [isExpanded]);

  return descriptionHtml ? (
    <div className="cp-bib-description">
      <div
        className="expandable"
        id={expandableContainerId}
        ref={expandableContainer}
        style={{ maxHeight: isExpanded ? '' : `${collapsedHeightPx}px` }}
      >
        {/* eslint-disable-next-line react/no-danger */}
        <div dangerouslySetInnerHTML={{ __html: descriptionHtml }} ref={htmlContainer}></div>
      </div>
      {isExpandable && (
        <TextButton
          aria-expanded={isExpanded}
          aria-controls={expandableContainerId}
          className="toggle"
          dataKey="is-expanded-toggle"
          handleClick={handleToggleClick}
          leftAligned
          type="button"
        >
          <span aria-hidden>
            <FormattedMessage id={isExpanded ? 'read_less' : 'read_more'} />
          </span>
          <ScreenReaderMessage>
            <FormattedMessage id={`${isExpanded ? 'read_less' : 'read_more'}_about_the_title`} />
          </ScreenReaderMessage>
          {isExpanded ? <ChevronUp /> : <ChevronDown />}
        </TextButton>
      )}
    </div>
  ) : null;
};

BibDescription.propTypes = {
  descriptionHtml: PropTypes.string.isRequired,
  uniqueIdPrefix: PropTypes.string.isRequired
};

export default BibDescription;
